import {
  Button,
  Circle,
  Collapse,
  HStack,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Spinner,
  StackDivider,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { PiCreditCard, PiScooter, PiSuitcaseRolling } from "react-icons/pi";

import { ChevronDownIcon } from "@chakra-ui/icons";
import { Elements } from "@stripe/react-stripe-js";
import React from "react";
import { Helmet } from "react-helmet";
import { CiCreditCardOff } from "react-icons/ci";
import { GiHouseKeys } from "react-icons/gi";
import { stripePromise } from "..";
import { images } from "../assets/images/images";
import EmptyState from "../components/EmptyState";
import SetupPaymentMethod from "../components/SetupPaymentMethod";
import { ENV } from "../constants/environment.base";
import { useBooking } from "../hooks/useBooking";
import { ILocker } from "../types/types";

const USAGE_ICONS = {
  luggage: PiSuitcaseRolling,
  scooter: PiScooter,
  belongings: GiHouseKeys,
};

const Booking: React.FC = () => {
  const {
    selectedLockerPricing,
    bookingsLoading,
    startBooking,
    usage,
    setUsage,
    size,
    setSize,
    chosenPaymentMethods,
    setChosenPaymentMethods,
    freeLockers,
    userPaymentMethodsLoading,
    userPaymentMethods,
    stripeClientSecret,
    isSetupPaymentMethodOpen,
    onSetupPaymentMethodOpen,
    onSetupPaymentMethodSuccess,
    onSetupPaymentMethodClose,
    valid,
    startBookingLoading,
  } = useBooking();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Booking - SALA</title>
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <VStack w={"full"} spacing={8} alignItems={"center"} p={4}>
        <VStack
          spacing={8}
          w={"full"}
          maxW={"700px"}
          as={"form"}
          onSubmit={(e) => {
            e.preventDefault();

            if (!usage || !size || !chosenPaymentMethods) return;

            startBooking({
              usage,
              size,
              payment_method: chosenPaymentMethods.id,
            });
          }}
        >
          <VStack alignItems={"start"} w={"full"}>
            <Text fontWeight={"bold"} fontSize={"2xl"}>
              Tipo de taquilla
            </Text>
            <VStack w={"full"}>
              {Object.keys(ENV.CAPTIONS.usage).map((usage_key) => (
                <Button
                  key={usage_key}
                  isDisabled={
                    (freeLockers &&
                      Object.values(freeLockers[usage_key] ?? {}).reduce(
                        (acc, cur) => acc + cur,
                        0
                      ) <= 0) ||
                    (freeLockers && !freeLockers[usage_key])
                  }
                  isActive={usage === usage_key}
                  _active={{
                    bgColor: "brand.500",
                    color: "white",
                  }}
                  justifyContent={"start"}
                  fontWeight={"medium"}
                  px={4}
                  py={2}
                  w={"full"}
                  h={"fit-content"}
                  borderRadius={"xl"}
                  bgColor={"gray.100"}
                  color={"black"}
                  onClick={() => {
                    freeLockers &&
                      Object.values(freeLockers[usage_key] ?? {}).reduce(
                        (acc, cur) => acc + cur,
                        0
                      ) > 0 &&
                      setUsage(usage_key as ILocker["usage"]);
                  }}
                >
                  <HStack spacing={4} alignItems={"center"}>
                    <Circle size={10} bg={"gray.50"}>
                      <Icon
                        as={USAGE_ICONS[usage_key as ILocker["usage"]]}
                        boxSize={"50%"}
                        color={"black"}
                      />
                    </Circle>

                    <VStack alignItems={"start"} spacing={2} py={3}>
                      <HStack>
                        <Text lineHeight={1} fontWeight={"semibold"}>
                          {ENV.CAPTIONS.usage[usage_key]}
                        </Text>

                        {freeLockers && (
                          <Tag
                            size={"sm"}
                            variant={"subtle"}
                            colorScheme={
                              Object.values(
                                freeLockers[usage_key] ?? {}
                              ).reduce((acc, cur) => acc + cur, 0) > 0
                                ? "green"
                                : "red"
                            }
                          >
                            ¡
                            {Object.values(freeLockers[usage_key] ?? {}).reduce(
                              (acc, cur) => acc + cur,
                              0
                            )}{" "}
                            disponibles!
                          </Tag>
                        )}
                      </HStack>

                      <Text
                        lineHeight={1}
                        fontSize={"sm"}
                        color={usage_key === usage ? "white" : "gray.500"}
                      >
                        {ENV.CAPTIONS.usage[usage_key]}
                      </Text>
                    </VStack>
                  </HStack>
                </Button>
              ))}
            </VStack>
          </VStack>

          <Collapse
            in={usage === "luggage"}
            animateOpacity
            style={{
              width: "100%",
            }}
          >
            <VStack alignItems={"start"} w={"full"}>
              <Text fontWeight={"bold"} fontSize={"2xl"}>
                Tamaño
              </Text>
              <SimpleGrid
                minChildWidth={{ base: "180px" }}
                w={"full"}
                spacing={4}
              >
                {["M", "L", "XL"].map((size_key) => (
                  <Button
                    key={size_key}
                    isDisabled={
                      freeLockers &&
                      freeLockers[usage] &&
                      freeLockers[usage][size_key] <= 0
                    }
                    isActive={size === size_key}
                    _active={{
                      bgColor: "brand.500",
                      color: "white",
                    }}
                    fontWeight={"medium"}
                    p={6}
                    w={"full"}
                    h={"fit-content"}
                    borderRadius={"xl"}
                    bgColor={"gray.100"}
                    color={"black"}
                    onClick={() => {
                      freeLockers &&
                        freeLockers[usage] &&
                        freeLockers[usage][size_key] > 0 &&
                        setSize(size_key as ILocker["size"]);
                    }}
                  >
                    <VStack
                      justifyContent={"center"}
                      alignItems={"center"}
                      h={"full"}
                      spacing={4}
                    >
                      <Image
                        src={images[`locker_${size_key.toLowerCase()}_icon`]}
                        h={"100px"}
                      />

                      <VStack
                        justifyContent={"end"}
                        alignItems={"center"}
                        spacing={2}
                      >
                        <Text
                          lineHeight={1}
                          fontWeight={"semibold"}
                          fontSize={"lg"}
                        >
                          Tamaño {size_key}
                        </Text>

                        <Text>
                          {ENV.LUGGAGE_LOCKERS_DIMENSIONS[size_key].height}cm x{" "}
                          {ENV.LUGGAGE_LOCKERS_DIMENSIONS[size_key].width}cm x{" "}
                          {ENV.LUGGAGE_LOCKERS_DIMENSIONS[size_key].depth}cm
                        </Text>

                        {freeLockers && freeLockers[usage] && (
                          <Tag
                            size={"sm"}
                            variant={"subtle"}
                            colorScheme={
                              freeLockers[usage][size_key] > 0 ? "green" : "red"
                            }
                          >
                            ¡{freeLockers[usage][size_key]} disponibles!
                          </Tag>
                        )}
                      </VStack>
                    </VStack>
                  </Button>
                ))}
              </SimpleGrid>
            </VStack>
          </Collapse>

          {/* Payment method select */}
          <VStack alignItems={"start"} w={"full"}>
            <Text fontWeight={"bold"} fontSize={"2xl"}>
              Método de pago
            </Text>
            {userPaymentMethodsLoading ? (
              <HStack>
                <Spinner />
                <Text>Cargando métodos de pago... </Text>
              </HStack>
            ) : !userPaymentMethods || !chosenPaymentMethods ? (
              <>
                <EmptyState
                  size="sm"
                  bgColor="gray.100"
                  icon={{
                    color: "brand",
                    icon: CiCreditCardOff,
                  }}
                  title={"No tienes ninguna tarjeta guardada"}
                  description={
                    "Añade una tarjeta para poder realizar pagos en SALA."
                  }
                  action={{
                    label: "Añadir tarjeta",
                    onClick: () => onSetupPaymentMethodOpen(),
                    isDisabled: !stripeClientSecret,
                    colorScheme: "brand",
                  }}
                />

                {stripeClientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: stripeClientSecret,
                      loader: "always",
                      locale: "es",
                      appearance: {
                        variables: {
                          colorPrimary: "#AD31C1",
                          borderRadius: "16px",
                        },
                      },
                    }}
                  >
                    <SetupPaymentMethod
                      isOpen={isSetupPaymentMethodOpen}
                      onClose={onSetupPaymentMethodClose}
                      onSuccess={onSetupPaymentMethodSuccess}
                    />
                  </Elements>
                )}
              </>
            ) : (
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  w={"full"}
                  h={"fit-content"}
                  px={6}
                  py={4}
                  borderRadius={"xl"}
                >
                  <HStack spacing={6}>
                    <Circle size={12} bg={"gray.50"}>
                      <Icon as={PiCreditCard} boxSize={"60%"} />
                    </Circle>
                    <VStack alignItems={"start"} spacing={1} py={1}>
                      <Text fontWeight={"semibold"} fontSize={"16px"}>
                        {chosenPaymentMethods
                          .card!.brand.charAt(0)
                          .toUpperCase() +
                          chosenPaymentMethods.card!.brand.slice(1)}{" "}
                        acabada en {chosenPaymentMethods?.card?.last4}
                      </Text>
                      <Text
                        color={"gray.700"}
                        fontWeight={"normal"}
                        size={"sm"}
                      >
                        Exp. {chosenPaymentMethods?.card?.exp_month}/
                        {chosenPaymentMethods?.card?.exp_year}
                      </Text>
                    </VStack>
                  </HStack>
                </MenuButton>
                <MenuList w={"full"}>
                  {userPaymentMethods.map((paymentMethod) => (
                    <MenuItem
                      key={paymentMethod.id}
                      minH="40px"
                      pr={12}
                      onClick={() => setChosenPaymentMethods(paymentMethod)}
                    >
                      <HStack spacing={4}>
                        <Circle size={10} bg={"gray.50"}>
                          <Icon as={PiCreditCard} boxSize={"50%"} />
                        </Circle>
                        <VStack alignItems={"start"} spacing={1}>
                          <Text fontWeight={"semibold"} fontSize={"16px"}>
                            {paymentMethod.card!.brand.charAt(0).toUpperCase() +
                              paymentMethod.card!.brand.slice(1)}{" "}
                            acabada en {paymentMethod.card!.last4}
                          </Text>
                          <Text
                            color={"gray.700"}
                            fontWeight={"normal"}
                            size={"sm"}
                          >
                            Exp. {paymentMethod.card!.exp_month}/
                            {paymentMethod.card!.exp_year}
                          </Text>
                        </VStack>
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </VStack>

          {/* Price detail */}
          <VStack spacing={4} alignItems={"start"} w={"full"}>
            <VStack
              spacing={2}
              justifyContent={"start"}
              alignItems={"start"}
              w={"full"}
            >
              <Text fontWeight={"bold"} fontSize={"2xl"}>
                Desglose del precio
              </Text>
              <Text fontSize={"small"} color={"gray.600"}>
                Recuerda que el precio final se calcula en base al tiempo de uso
                y el tipo de locker.
              </Text>
            </VStack>

            <VStack
              spacing={3}
              justifyContent={"start"}
              alignItems={"start"}
              w={"full"}
            >
              <VStack
                spacing={0}
                justifyContent={"start"}
                alignItems={"start"}
                w={"full"}
                bgColor={"gray.100"}
                borderRadius={"xl"}
                px={4}
                py={3}
              >
                <Text color={"gray.600"} fontSize={"small"}>
                  Precio base 1ª hora
                </Text>
                <Text fontWeight={"semibold"} fontSize={"lg"}>
                  {selectedLockerPricing.base
                    ? selectedLockerPricing.base.toLocaleString("es-ES", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : "-"}
                </Text>
              </VStack>

              <VStack
                spacing={0}
                w={"full"}
                divider={<StackDivider borderColor={"gray.200"} />}
                bgColor={"gray.100"}
                borderRadius={"xl"}
              >
                {[
                  {
                    caption: "(T1) De la 2ª a la 5ª hora",
                    value: selectedLockerPricing["1"] ?? "-",
                  },
                  {
                    caption: "(T2) De la 6ª a la 24ª hora",
                    value: selectedLockerPricing["5"] ?? "-",
                  },
                  {
                    caption: "(T3) A partir de la 24ª hora",
                    value: selectedLockerPricing["24"] ?? "-",
                  },
                ].map((row) => (
                  <VStack
                    key={row.caption}
                    spacing={0}
                    justifyContent={"start"}
                    alignItems={"start"}
                    w={"full"}
                    px={4}
                    py={3}
                  >
                    <Text color={"gray.600"} fontSize={"small"}>
                      {row.caption}
                    </Text>
                    <Text fontWeight={"semibold"} fontSize={"lg"}>
                      {row.value.toLocaleString("es-ES", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      / hora
                    </Text>
                  </VStack>
                ))}
              </VStack>
            </VStack>
          </VStack>

          <VStack spacing={8} w={"full"}>
            <Text fontSize={"14px"} color={"gray.600"}>
              El precio se calcula de la siguiente manera: <br /> Precio base +
              (T1 x horas) + (T2 x horas) + (T3 x horas) = Precio final <br />
              <br />
              No se te cobrará nada hasta que no finalizes tu reserva. El cargo
              mínimo es de 0,50€. Se cobrarán horas completas de reserva (por
              ejemplo, si reservas 30 minutos, se cobrará 1 hora).
            </Text>
            <Button
              type="submit"
              colorScheme={"brand"}
              size={"lg"}
              variant={"solid"}
              w={"full"}
              maxW={{ base: "unset", md: "300px" }}
              isDisabled={!valid}
              isLoading={startBookingLoading || bookingsLoading}
              loadingText={"Reservando..."}
            >
              Iniciar reserva
            </Button>
          </VStack>
        </VStack>
      </VStack>
    </>
  );
};

export default Booking;
